/**
 * DriverCockpit — premium, map-first cockpit chrome (presentation only).
 *
 * Renders a full-screen glass overlay ON TOP of the live map: a status ribbon,
 * one context-sensitive primary action button (idle/offline states only), a
 * slide-up ride-offer sheet, a repositioned SOS slot, a swipeable KPI strip and
 * a clean bottom nav. During an active ride it hosts the existing rich
 * `activeRideSlot` (ActiveRideCard) in a bottom sheet so navigate / call / chat
 * / live-fare / arrival / start / complete are all preserved.
 *
 * It owns NO business logic — every action is delegated to handlers passed from
 * Driver.jsx (toggleOnline / accept / decline / arrived / start / complete), so
 * dispatch, lifecycle, state-machine, offer-routing and payment code are
 * untouched. Dark "Performance Pro" aesthetic per the approved blueprint.
 */
import React, { useEffect, useState } from "react";
import {
  Power, Star, TrendingUp, Car, Home, Wallet, User,
  Volume2, VolumeX, Check, X, Clock, Lock,
} from "lucide-react";

import { useDriverFinancialPrivacy } from "../../lib/driverFinancialPrivacy";

const money = (n) => (n == null ? "—" : `£${Number(n).toFixed(2)}`);

function initials(name) {
  if (!name) return "DR";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");
}

const StatusRibbon = ({ isOnline, busy, onToggle, onlineForLabel, todayOnlineLabel }) => {
  const cfg = busy
    ? { dot: "#F59E0B", label: "BUSY", ring: "border-[#F59E0B]/50" }
    : isOnline
      ? { dot: "#22C55E", label: "ONLINE", ring: "border-[#22C55E]/50" }
      : { dot: "#EF4444", label: "OFFLINE", ring: "border-[#EF4444]/50" };
  return (
    <div className="pointer-events-none absolute top-5 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-1">
      <button
        type="button"
        data-testid="driver-status-ribbon"
        onClick={busy ? undefined : onToggle}
        disabled={busy}
        className={`pointer-events-auto px-4 py-2 rounded-full
          bg-black/70 backdrop-blur-xl border ${cfg.ring} text-white text-sm font-bold flex items-center gap-2
          shadow-lg transition-transform active:scale-95`}
      >
        <span className="relative flex h-2.5 w-2.5">
          {isOnline && !busy && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: cfg.dot }} />}
          <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: cfg.dot }} />
        </span>
        {cfg.label}
        {isOnline && onlineForLabel && (
          <span className="text-white/80 font-semibold" data-testid="driver-online-for">· {onlineForLabel}</span>
        )}
      </button>
      {isOnline && todayOnlineLabel && (
        <span
          data-testid="driver-online-today-total"
          className="px-2.5 py-0.5 rounded-full bg-black/55 backdrop-blur-md text-white/75 text-[11px] font-medium"
        >
          Today total: {todayOnlineLabel}
        </span>
      )}
    </div>
  );
};

const KpiStrip = ({ cycle, myRating, completedCount, onNav }) => {
  const { masked } = useDriverFinancialPrivacy();
  const earnings = cycle?.today?.gross_earnings ?? cycle?.today?.net_earnings ?? null;
  const trips = completedCount ?? 0;
  const rating = myRating?.avg_rating ?? null;
  const cards = [
    { id: "earnings", icon: Wallet, label: "Earnings today", value: masked ? "••••" : money(earnings), tint: "#22C55E", locked: masked },
    { id: "trips", icon: TrendingUp, label: "Trips", value: trips, tint: "#007AFF" },
    { id: "rating", icon: Star, label: "Rating", value: rating != null ? Number(rating).toFixed(2) : "—", tint: "#F59E0B" },
  ];
  return (
    <div data-testid="kpi-scroll-panel" className="pointer-events-auto flex overflow-x-auto snap-x snap-mandatory gap-3 pb-3 px-4 no-scrollbar">
      {cards.map((c) => (
        <div key={c.id} data-testid={`kpi-card-${c.id}`}
          onClick={c.locked ? () => onNav?.("earnings") : undefined}
          className={`snap-center shrink-0 w-[150px] bg-black/60 backdrop-blur-xl border border-white/12 rounded-2xl p-3 flex flex-col gap-1 ${c.locked ? "cursor-pointer" : ""}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400">
            <c.icon className="w-3.5 h-3.5" style={{ color: c.tint }} /> {c.label}
          </div>
          <div className="text-2xl font-bold text-white tabular-nums flex items-center gap-1.5">
            {c.locked && <Lock className="w-3.5 h-3.5 text-zinc-500" />}{c.value}</div>
        </div>
      ))}
    </div>
  );
};

function OfferCountdown({ expiresAt }) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!expiresAt) { setLeft(null); return; }
    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      setLeft(Math.max(0, Math.round(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (left == null) return null;
  return (
    <span data-testid="cockpit-offer-countdown" className="flex items-center gap-1 text-[#F59E0B] font-bold text-sm tabular-nums">
      <Clock className="w-4 h-4" /> {left}s
    </span>
  );
}

const RideOfferSheet = ({ offer, onAccept, onDecline }) => {
  const km = offer.pickup_distance_km ?? offer.distance_km;
  const dist = offer.distance_mi != null ? `${offer.distance_mi} mi` : (offer.distance_km != null ? `${offer.distance_km} km` : "—");
  return (
    <div className="pointer-events-auto fixed inset-x-0 bottom-0 z-[60] animate-[cockpitSlideUp_.2s_ease-out]">
      <div data-testid="ride-offer-sheet" className="bg-[#141414] rounded-t-3xl border-t border-white/12 p-6 pb-8 shadow-[0_-10px_40px_rgba(0,0,0,0.6)]">
        <div className="w-12 h-1.5 bg-zinc-700 rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#22C55E] animate-pulse">New ride offer</div>
          <OfferCountdown expiresAt={offer.offer_expires_at} />
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="text-white font-bold text-lg truncate" data-testid="cockpit-offer-passenger">{offer.passenger_name || "Passenger"}</div>
          <div className="text-2xl font-bold text-white tabular-nums">{money(offer.estimated_fare ?? offer.fare)}</div>
        </div>
        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase text-zinc-500 font-bold">Pickup{km != null ? ` · ${Number(km).toFixed(1)} km away` : ""}</div>
              <div className="text-white font-medium truncate">{offer.pickup_address || "—"}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-1 w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase text-zinc-500 font-bold">Drop-off</div>
              <div className="text-white font-medium truncate">{offer.dropoff_address || "—"}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 mb-5 text-xs text-zinc-400 font-semibold">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#007AFF]" /> {offer.eta_min || "—"} min</span>
          <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5 text-[#007AFF]" /> {dist}</span>
          {offer.passenger_count ? <span className="flex items-center gap-1"><User className="w-3.5 h-3.5 text-[#007AFF]" /> {offer.passenger_count} pax</span> : null}
        </div>
        <div className="flex gap-3">
          <button data-testid="cockpit-decline-offer" onClick={onDecline}
            className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 text-white flex items-center justify-center active:scale-95 transition-transform">
            <X className="w-6 h-6" />
          </button>
          <button data-testid="cockpit-accept-offer" onClick={onAccept}
            className="flex-1 h-14 rounded-2xl bg-[#22C55E] text-black text-lg font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-[0_0_24px_rgba(34,197,94,0.4)]">
            <Check className="w-6 h-6" /> Accept Ride
          </button>
        </div>
      </div>
    </div>
  );
};

const BottomNav = ({ onNav, active = "home" }) => {
  const items = [
    { key: "home", label: "Home", icon: Home },
    { key: "earnings", label: "Earnings", icon: Wallet },
    { key: "trips", label: "Trips", icon: Car },
    { key: "account", label: "Account", icon: User },
  ];
  return (
    <nav data-testid="cockpit-bottom-nav" className="pointer-events-auto mt-2 mx-3 mb-3 rounded-2xl bg-black/70 backdrop-blur-xl border border-white/12 flex items-center justify-around py-2">
      {items.map((it) => (
        <button key={it.key} data-testid={`cockpit-nav-${it.key}`} onClick={() => onNav?.(it.key)}
          className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${active === it.key ? "text-white" : "text-zinc-500"}`}>
          <it.icon className="w-5 h-5" />
          <span className="text-[10px] font-semibold">{it.label}</span>
        </button>
      ))}
    </nav>
  );
};

export function DriverCockpit({
  isOnline, onlineForLabel, todayOnlineLabel, activeRide, pendingOffer,
  cycle, myRating, completedCount,
  ringtoneMuted,
  driverName,
  onToggleOnline, onAccept, onDecline,
  onToggleRingtone, onNav, activeTab = "home",
  sosSlot, activeRideSlot, completedSlot, banners,
}) {
  const busy = !!activeRide;
  const idleAction = !isOnline
    ? { key: "go-online", label: "Go Online", sub: "Start receiving ride offers", cls: "bg-[#007AFF] text-white" }
    : { key: "idle", label: "Waiting for ride offers", sub: "You're online — tap the ribbon to go offline", cls: "bg-[#1F1F1F] text-white border border-white/15", disabled: true };

  return (
    <div className="pointer-events-none fixed inset-0 z-40" data-testid="driver-cockpit">
      {/* Top floating controls */}
      <StatusRibbon isOnline={isOnline} busy={busy} onToggle={() => onToggleOnline?.(!isOnline)}
        onlineForLabel={onlineForLabel} todayOnlineLabel={todayOnlineLabel} />

      {/* Profile / account trigger — top-left */}
      <button
        type="button"
        data-testid="profile-menu-button"
        onClick={() => onNav?.("account")}
        className="pointer-events-auto absolute top-5 left-4 z-50 w-11 h-11 rounded-full bg-black/60 backdrop-blur-xl border border-white/15 text-white font-bold text-sm flex items-center justify-center active:scale-95 transition-transform"
      >
        {initials(driverName)}
      </button>

      {/* SOS slot — top-right */}
      {sosSlot && <div className="pointer-events-auto absolute top-5 right-4 z-50">{sosSlot}</div>}

      {/* Ringtone utility — right, below SOS */}
      <div className="pointer-events-auto absolute top-20 right-4 z-40 flex flex-col items-center gap-1">
        <button data-testid="cockpit-ringtone-toggle" onClick={onToggleRingtone}
          aria-pressed={!ringtoneMuted}
          aria-label={ringtoneMuted ? "Sound alerts are OFF — tap to turn on" : "Sound alerts are ON — tap to mute"}
          title={ringtoneMuted ? "Sound alerts OFF — tap to turn on" : "Sound alerts ON — tap to mute"}
          className={`w-11 h-11 rounded-full bg-black/60 backdrop-blur-xl border flex items-center justify-center transition-colors
            ${ringtoneMuted ? "border-zinc-600 text-zinc-400" : "border-[#22C55E]/50 text-[#22C55E]"}`}>
          {ringtoneMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        {ringtoneMuted && (
          <span data-testid="cockpit-ringtone-muted-label"
            className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 leading-none">Muted</span>
        )}
      </div>

      {/* Floating status banners (offline / enable-sound / stale connection) */}
      {banners && (
        <div className="pointer-events-auto absolute top-16 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md space-y-2">
          {banners}
        </div>
      )}

      {/* Ride offer takes over the bottom thumb-zone */}
      {pendingOffer ? (
        <RideOfferSheet offer={pendingOffer} onAccept={onAccept} onDecline={onDecline} />
      ) : activeRideSlot ? (
        <div
          className="pointer-events-auto absolute inset-x-0 bottom-0 z-[45] max-h-[72vh] overflow-y-auto px-3 pb-3 pt-2 no-scrollbar"
          data-testid="cockpit-active-ride-sheet"
        >
          {activeRideSlot}
        </div>
      ) : (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40">
          {completedSlot && (
            <div className="pointer-events-auto px-3 pb-2 max-h-[60vh] overflow-y-auto no-scrollbar">{completedSlot}</div>
          )}
          <KpiStrip cycle={cycle} myRating={myRating} completedCount={completedCount} onNav={onNav} />
          <div className="pointer-events-auto px-4">
            <button
              type="button"
              data-testid="main-action-button"
              onClick={() => { if (!idleAction.disabled) onToggleOnline?.(true); }}
              disabled={idleAction.disabled}
              className={`w-full h-16 rounded-full ${idleAction.cls} text-xl font-bold flex flex-col items-center justify-center
                transition-transform active:scale-95 shadow-[0_8px_30px_rgba(0,0,0,0.5)] ${idleAction.disabled ? "opacity-90 cursor-default" : ""}`}
            >
              <span className="flex items-center gap-2 leading-none">
                {idleAction.key === "go-online" ? <Power className="w-5 h-5" /> : <Car className="w-5 h-5" />} {idleAction.label}
              </span>
              {idleAction.sub && <span className="text-[11px] font-medium opacity-80 mt-0.5">{idleAction.sub}</span>}
            </button>
          </div>
          <BottomNav onNav={onNav} active={activeTab} />
        </div>
      )}
    </div>
  );
}

export default DriverCockpit;
